import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { kSourceCaseLawHtmlV3 } from '@/constants/constants-api-paths'
import { nanoid } from 'nanoid'
import { getAuth } from 'firebase/auth'
import { kErrorCodeAuthError } from '@/constants/constants-error-codes'
import { ApiError } from '@/util/errors/api-error'
import * as Sentry from '@sentry/browser'
import { CaselawReferenceHtmlResponseSchemaV3, CaselawReferenceHtmlResponseV3 } from '../schema/caselaw-reference-viewer.schema'

/**
 * Get Caselaw Reference HTML V3
 * @param s3_path - The s3 path of the caselaw reference
 * @returns The caselaw reference HTML
 */
export async function getCaselawReferenceHtmlForStandaloneViewerV3(s3_path: string): Promise<CaselawReferenceHtmlResponseV3> {
  const sentry_transaction_id = nanoid()
  const token = await getAuth().currentUser?.getIdToken()
  if (!token) throw Error(kErrorCodeAuthError)

  try {
    const apiUrl = kPaxtonAppApiRESTBaseUrl() + kSourceCaseLawHtmlV3 + '?s3_path=' + s3_path

    const requestOptions: RequestInit = {
      headers: {
        'Content-Type': 'application/json; charset=UTF-8',
        'X-Transaction-Id': sentry_transaction_id,
        Authorization: `Bearer ${token}`,
      },
      method: 'GET',
    }

    const response = await fetch(apiUrl, requestOptions)
    const data = await response.json()

    // Validate the data against the schema
    const validatedData = CaselawReferenceHtmlResponseSchemaV3.parse(data)

    if (!response.ok) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentry_transaction_id })

        Sentry.captureException(new Error(`Fetch error - status: ${response.status}, detail: ${JSON.stringify(data.detail)}`), {
          extra: {
            requestUrl: apiUrl,
            requestOptions: requestOptions,
            response: JSON.stringify(response),
            data: JSON.stringify(data),
            onLine: navigator.onLine,
            cookieEnabled: navigator.cookieEnabled,
          },
        })
      })
      throw new ApiError(response.status, `Fetch error - status: ${response.status}, detail: ${JSON.stringify(data.detail)}`)
    }

    return validatedData
  } catch (error) {
    if (!(error instanceof ApiError)) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentry_transaction_id })

        Sentry.captureException(error, {
          extra: { onLine: navigator.onLine, cookieEnabled: navigator.cookieEnabled },
        })
      })
    }
    throw error
  }
}
